import { Box, Flex } from '@chakra-ui/react';
import { faBuilding } from '@fortawesome/pro-light-svg-icons/faBuilding';
import { faBurst } from '@fortawesome/pro-light-svg-icons/faBurst';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faCircleUser } from '@fortawesome/pro-light-svg-icons/faCircleUser';
import { faHome } from '@fortawesome/pro-light-svg-icons/faHome';
import { faIdCard } from '@fortawesome/pro-light-svg-icons/faIdCard';
import { faMoneyBillWave } from '@fortawesome/pro-light-svg-icons/faMoneyBillWave';
import { faSquareInfo } from '@fortawesome/pro-light-svg-icons/faSquareInfo';
import { faUmbrella } from '@fortawesome/pro-light-svg-icons/faUmbrella';
import type { ModuleRegisterFunction, Runtime } from '@sgi/af-shell-shared';
import { msalInstance } from '@sgi/af-shell-shared';
import { FAIcon } from '@sgi/gravity/FAIcon';
import { Typography } from '@sgi/gravity/Typography';
import { apiBaseQuery } from 'app/api/apiUtils';
import { bffEndpoints, uiPaths } from 'properties/appProperties';
import { Link } from 'react-router-dom';
import type { CustomerAuthClaims } from 'types/internal/CustomerAuthClaims';
import { getConfig } from './config';

export const registerNav: ModuleRegisterFunction = async (runtime: Runtime) => {
  let isLoggedIn = false;
  if (msalInstance?.getActiveAccount()) {
    isLoggedIn = true;
  }

  let authClaims: CustomerAuthClaims | undefined = undefined;
  let isDisabledForApril30thRelease = true;

  if (isLoggedIn) {
    const featureFlagKey = 'customer-portal-disable-for-april-30th-release';
    isDisabledForApril30thRelease = await apiBaseQuery(
      `${bffEndpoints.GET_AUTHORIZED_FEATURE_FLAG_STATUS}/${featureFlagKey}`,
      'GET',
    );

    authClaims = await apiBaseQuery(`${bffEndpoints.GET_CUSTOMER_AUTH_CLAIMS}`, 'GET');
  }

  const config = await getConfig();

  runtime.registerNavigationItems([
    {
      to: `${uiPaths.ROOT}/${uiPaths.CUSTOMER_HOME}`,
      itemContent: 'Home',
      additionalProps: { icon: faHome },
      priority: 0,
    },
    {
      to: '',
      itemContent: 'Claims',
      isVisible: true,
      additionalProps: { icon: faBurst },
      priority: 12,
      children: [
        {
          to: '',
          itemContent: 'Claims',
          children: [
            {
              to: `/${uiPaths.CLAIMS}/${uiPaths.CLAIMS_SELECT_POLICY}`,
              itemContent: 'Submit a Claim',
            },
            {
              to: `${uiPaths.ROOT}/${uiPaths.CUSTOMER_CLAIMS}`,
              itemContent: 'View My Claims',
            },
          ],
        },
      ],
    },
    {
      to: '',
      itemContent: 'Policies',
      isVisible: true,
      additionalProps: { icon: faUmbrella },
      priority: 14,
      children: [
        {
          to: '',
          itemContent: 'Policies',
          children: [
            {
              to: `${uiPaths.ROOT}/${uiPaths.COVERAGE}`,
              itemContent: 'View My Policies',
            },
          ],
        },
      ],
    },
    {
      to: '',
      itemContent: 'Payments',
      isVisible: false,
      additionalProps: { icon: faMoneyBillWave },
      priority: 16,
      children: [
        {
          to: '',
          itemContent: 'Payments',
          children: [
            {
              to: '#',
              itemContent: 'Pay My Outstanding Items',
              additionalProps: { isExternal: true },
            },
          ],
        },
      ],
    },
  ]);

  runtime.registerNavigationItems([
    {
      to: '',
      itemContent: 'Licence',
      isVisible: authClaims?.isVerifiedAutofund && !isDisabledForApril30thRelease,
      additionalProps: { icon: faIdCard },
      priority: 10,
      children: [
        {
          to: '',
          itemContent: 'Licence',
          children: [
            {
              to: config.MYSGI_URL,
              itemContent: 'Manage my Driver Information (Classic MySGI)',
              additionalProps: { isExternal: false },
            },
          ],
        },
      ],
    },
    {
      to: `${uiPaths.ROOT}/Support`,
      itemContent: 'Support',
      isVisible: authClaims?.isVerifiedAutofund && !isDisabledForApril30thRelease,
      additionalProps: { icon: faSquareInfo },
      priority: 30,
    },
  ]);

  const userMenuItems =
    authClaims?.isVerifiedSgic && !authClaims?.isVerifiedAutofund // SGI Canada only
      ? [
          {
            component: (
              <Link to={`${uiPaths.ROOT}/${uiPaths.CUSTOMER_PROFILE}`} style={{ width: '100%' }}>
                <Box w={'100%'}>
                  <Flex flexDir="row" gap="3">
                    <FAIcon
                      size="2xl"
                      data-testid="manage-profile-icon"
                      paddingRight={1}
                      color="teal.500"
                      icon={faCircleUser}
                    />
                    <Typography variant="bodyBold">Manage Profile</Typography>
                  </Flex>
                </Box>
              </Link>
            ),
            category: '',
          },
          {
            component: (
              <Link to={`${uiPaths.ROOT}/${uiPaths.CONTACT_US}`} style={{ width: '100%' }}>
                <Box w={'100%'}>
                  <Flex flexDir="row" gap="3">
                    <FAIcon
                      size="2xl"
                      data-testid="my-issuer-and-broker-icon"
                      paddingRight={1}
                      color="teal.500"
                      icon={faBuilding}
                    />
                    <Typography variant="bodyBold">My Broker</Typography>
                  </Flex>
                </Box>
              </Link>
            ),
            category: '',
          },
        ]
      : [
          {
            component: (
              <Link to={`${uiPaths.ROOT}/${uiPaths.CUSTOMER_PROFILE}`} style={{ width: '100%' }}>
                <Box w={'100%'}>
                  <Flex flexDir="row" gap="3">
                    <FAIcon
                      size="2xl"
                      data-testid="manage-profile-icon"
                      paddingRight={1}
                      color="teal.500"
                      icon={faCircleUser}
                    />
                    <Typography variant="bodyBold">Manage Profile</Typography>
                  </Flex>
                </Box>
              </Link>
            ),
            category: '',
          },
          {
            component: (
              <Link to={`${uiPaths.ROOT}/${uiPaths.CONTACT_US}`} style={{ width: '100%' }}>
                <Box w={'100%'}>
                  <Flex flexDir="row" gap="3">
                    <FAIcon
                      size="2xl"
                      data-testid="my-issuer-and-broker-icon"
                      paddingRight={1}
                      color="teal.500"
                      icon={faBuilding}
                    />
                    <Typography variant="bodyBold">My Motor Licence Issuer and Broker</Typography>
                  </Flex>
                </Box>
              </Link>
            ),
            category: '',
          },
          {
            component: (
              <Link to={`${uiPaths.ROOT}/${uiPaths.SUPPORT}`} style={{ width: '100%' }}>
                <Box w={'100%'}>
                  <Flex flexDir="row" gap="3">
                    <FAIcon
                      size="2xl"
                      data-testid="support-icon"
                      paddingRight={1}
                      color="teal.500"
                      icon={faCircleInfo}
                    />
                    <Typography variant="bodyBold">Support</Typography>
                  </Flex>
                </Box>
              </Link>
            ),
            category: '',
          },
        ];

  runtime.registerUserMenuItems(userMenuItems);
};
